/**
 * PERMISSIONS — the single source of truth for permission identifiers.
 *
 * Both the FE (to decide which UI controls to render) and the BE (to
 * enforce RBAC in middleware) import this constant. Drift between the
 * two is a class of bug; the type system catches it because the
 * permission strings are typed as `Permission` literal-union.
 *
 * Format: `<resource>.<action>`. Resources are the top-level noun;
 * actions are verbs in lower case. We do NOT encode tenant scoping
 * here — that is implicit (every check runs inside a tenant context,
 * and RLS is the actual enforcement). We only encode "can this role
 * perform this action on a resource within the current tenant".
 *
 * Permission IDs are STABLE once published — renaming one is a
 * breaking change for both clients. Adding new permissions is fine.
 */
export const PERMISSIONS = {
  USERS: {
    VIEW: 'users.view',
    CREATE: 'users.create',
    UPDATE: 'users.update',
    DELETE: 'users.delete',
    MANAGE_MEMBERSHIPS: 'users.manage_memberships',
    /**
     * Sprint 3 PR#3a — admin-only access to the roles catalogue page
     * (`/roles`). The route layer wires this constant via
     * `requirePermission(PERMISSIONS.USERS.MANAGE_ROLES)`. Not
     * granted to SELLER/WAREHOUSE in `packages/domain/src/roles.ts`;
     * ADMIN gets it implicitly via `ALL_PERMISSION_VALUES`. The
     * `LeafValues<T>` recursive type picks up the new constant so
     * the `Permission` literal-union widens automatically.
     */
    MANAGE_ROLES: 'users.manage_roles',
  },
  TENANTS: {
    VIEW: 'tenants.view',
    UPDATE: 'tenants.update',
    MANAGE_SETTINGS: 'tenants.manage_settings',
  },
  CATALOG: {
    VIEW_PRODUCTS: 'catalog.view_products',
    CREATE_PRODUCT: 'catalog.create_product',
    UPDATE_PRODUCT: 'catalog.update_product',
    DELETE_PRODUCT: 'catalog.delete_product',
    MANAGE_BRANDS: 'catalog.manage_brands',
    MANAGE_PRODUCT_MODELS: 'catalog.manage_product_models',
    MANAGE_PRODUCT_TYPES: 'catalog.manage_product_types',
    MANAGE_CURRENCIES: 'catalog.manage_currencies',
    MANAGE_EXCHANGE_RATES: 'catalog.manage_exchange_rates',
    CREATE_EXCHANGE_RATE: 'catalog.create_exchange_rate',
    VIEW_PRICE_HISTORY: 'catalog.view_price_history',
  },
  CRM: {
    VIEW_CUSTOMERS: 'crm.view_customers',
    CREATE_CUSTOMERS: 'crm.create_customers',
    UPDATE_CUSTOMERS: 'crm.update_customers',
    DELETE_CUSTOMERS: 'crm.delete_customers',
    VIEW_CLIENTS: 'crm.view_clients',
    CREATE_CLIENT: 'crm.create_client',
    UPDATE_CLIENT: 'crm.update_client',
    DELETE_CLIENT: 'crm.delete_client',
    /**
     * Slice 2 — seller follow-up tasks (WhatsApp / call / visit).
     *
     * Single granular key for the whole task surface (list, create,
     * reschedule, complete, cancel) plus the manual daily-reminder
     * trigger. The scheduled `POST /seller-tasks/daily-reminders`
     * fan-out itself is ADMIN-only on top of this key (checked via
     * `auth.role`, not via a second permission) so a second catalog
     * entry is unnecessary.
     *
     * Granted to SELLER (daily workflow: own tasks) + ADMIN
     * (implicitly, via ALL_PERMISSION_VALUES). WAREHOUSE is
     * excluded — fulfillment never owns follow-up tasks.
     */
    MANAGE_TASKS: 'crm.manage_tasks',
    VIEW_PROVIDERS: 'crm.view_providers',
    CREATE_PROVIDER: 'crm.create_provider',
    UPDATE_PROVIDER: 'crm.update_provider',
    DELETE_PROVIDER: 'crm.delete_provider',
  },
  /**
   * HR — worker files (expediente) module.
   *
   * Naming follows the `<resource>.<action>` contract:
   *
   *   view_workers   — read access to the worker list + detail
   *                    (including the document list with signed
   *                    download URLs). Every HR-adjacent reader
   *                    needs this.
   *   manage_workers — create / edit / deactivate worker files +
   *                    upload expediente documents. ADMIN-only in
   *                    the role matrix (see packages/domain/roles.ts);
   *                    ADMIN gets both implicitly via
   *                    ALL_PERMISSION_VALUES.
   *
   * Payroll, commissions, tasks and CRM reports are explicitly OUT
   * of this group (later slices).
   */
  HR: {
    VIEW_WORKERS: 'hr.view_workers',
    MANAGE_WORKERS: 'hr.manage_workers',
  },
  INVENTORY: {
    VIEW: 'inventory.view',
    CREATE: 'inventory.create',
    UPDATE: 'inventory.update',
    /**
     * Slice 6 PR#3 — DELETE for the 3-level deletion flow (CAP-4).
     * ADMIN-only — destructive actions on stock history are admin-only.
     * The 'inventory.delete' permission is reused from Slice 0 (it
     * existed as a placeholder). ADMIN gets it implicitly via
     * ALL_PERMISSIONS (see ROLE_PERMISSIONS in packages/domain).
     */
    DELETE: 'inventory.delete',
    ADJUST_STOCK: 'inventory.adjust_stock',
    // Slice 2 — fine-grained permissions for the inventory workflow.
    // VIEW_ITEMS is the read-only list permission; VIEW_COSTS gates the
    // avg_cost column separately (a WAREHOUSE can see stock levels but
    // not unit costs). MANAGE_WAREHOUSES is the CRUD on warehouses.
    // CREATE_RECEIPT / VIEW_RECEIPTS / APPROVE_RECEIPT / REJECT_RECEIPT
    // split the receipt lifecycle; VIEW_MOVEMENTS exposes the audit
    // ledger without granting write access.
    VIEW_ITEMS: 'inventory.view_items',
    VIEW_COSTS: 'inventory.view_costs',
    MANAGE_WAREHOUSES: 'inventory.manage_warehouses',
    CREATE_RECEIPT: 'inventory.create_receipt',
    VIEW_RECEIPTS: 'inventory.view_receipts',
    APPROVE_RECEIPT: 'inventory.approve_receipt',
    REJECT_RECEIPT: 'inventory.reject_receipt',
    VIEW_MOVEMENTS: 'inventory.view_movements',
    /**
     * Anexos Feature B — Administrative override of inventory_items.
     * ADMIN-only. Lets a tenant admin patch non-critical fields
     * (currently only `serialNumber`) for emergency corrections
     * (typos, etc.). All edits are recorded in `audit_log` with
     * action='edit'. Other roles (WAREHOUSE, SELLER) are explicitly
     * excluded — they use the receive flow to create inventory, not
     * direct edit.
     */
    ADMIN_EDIT: 'inventory.admin_edit',
  },
  SALES: {
    VIEW_ORDERS: 'sales.view_orders',
    CREATE_ORDERS: 'sales.create_orders',
    UPDATE_ORDERS: 'sales.update_orders',
    CANCEL_ORDERS: 'sales.cancel_orders',
    // Slice 2 — fine-grained permissions for the sales workflow.
    // VIEW_ORDERS / CREATE_ORDER / UPDATE_ORDER / APPROVE_ORDER /
    // CLOSE_ORDER / CANCEL_ORDER cover the full sales_order lifecycle.
    // VIEW_QUOTATIONS / CREATE_QUOTATION / UPDATE_QUOTATION /
    // SEND_QUOTATION / CONVERT_QUOTATION cover the quotation flow.
    // VIEW_COMMISSION_RULES / MANAGE_COMMISSION_RULES are admin-only
    // (commission_rules is a low-volume lookup table).
    VIEW_ORDERS_DETAIL: 'sales.view_orders_detail',
    CREATE_ORDER: 'sales.create_order',
    UPDATE_ORDER: 'sales.update_order',
    APPROVE_ORDER: 'sales.approve_order',
    CLOSE_ORDER: 'sales.close_order',
    CANCEL_ORDER: 'sales.cancel_order',
    // Slice 6 PR#5 (CAP-3) — ADMIN-only retroactive close-date edit.
    // Distinct from UPDATE_ORDER because the close-date is a
    // historical correction (back-dating / past-dating) that
    // re-stamps the closedAt. Granting it broadly (e.g. SELLER)
    // would let any rep rewrite CRM history without an audit-ready
    // workflow. ADMIN gets it implicitly via ALL_PERMISSION_VALUES.
    UPDATE_CLOSE_DATE: 'sales.update_close_date',
    VIEW_QUOTATIONS: 'sales.view_quotations',
    CREATE_QUOTATION: 'sales.create_quotation',
    UPDATE_QUOTATION: 'sales.update_quotation',
    SEND_QUOTATION: 'sales.send_quotation',
    CONVERT_QUOTATION: 'sales.convert_quotation',
    VIEW_COMMISSION_RULES: 'sales.view_commission_rules',
    MANAGE_COMMISSION_RULES: 'sales.manage_commission_rules',
  },
  REPORTS: {
    VIEW: 'reports.view',
    EXPORT: 'reports.export',
  },
  /**
   * Slice 3 — Finance module permissions.
   *
   * Naming follows the `<resource>.<action>` contract. Resources map
   * 1:1 onto the new tables / views introduced in this PR:
   *
   *   receivables  → `receivable_items`
   *   payables     → `payable_items`
   *   collections  → `receivable_collections` + `collection_batches`
   *                  + `collection_allocations`
   *   accounts     → administrative CRUD on `bank_accounts`,
   *                  `recurring_expenses`, and the equity / reports
   *                  permissions that surface in the balance sheet.
   *
   * `finance.allocate` is split out from `finance.create_collection`
   * because allocation is a separate decision (the cashier allocates
   * an already-recorded collection to specific receivables). The
   * SELLER role gets view-receivables/payables but NOT allocate.
   *
   * `finance.exclude_from_projection` is the admin-only knob to mark
   * a receivable (or a client) outside the cash-flow forecast. It is
   * distinct from `finance.close_receivable` because exclusion is a
   * reporting concern, not a state-transition.
   */
  FINANCE: {
    VIEW_RECEIVABLES: 'finance.view_receivables',
    CREATE_RECEIVABLE: 'finance.create_receivable',
    UPDATE_RECEIVABLE: 'finance.update_receivable',
    CLOSE_RECEIVABLE: 'finance.close_receivable',
    EXCLUDE_FROM_PROJECTION: 'finance.exclude_from_projection',
    /**
     * Slice 6 PR#4 (CAP-5) — DELETE for the 3-level deletion flow.
     * ADMIN-only — destructive actions on receivables are admin-only.
     * The permission strings reuse the `<resource>.<action>` contract
     * (`finance.delete_receivable`, `finance.delete_payable`) so the
     * route layer's `requirePermission()` middleware can wire them
     * with no extra plumbing. ADMIN gets the permissions implicitly
     * via ALL_PERMISSIONS (see ROLE_PERMISSIONS in @repo/domain).
     */
    DELETE_RECEIVABLE: 'finance.delete_receivable',
    VIEW_PAYABLES: 'finance.view_payables',
    CREATE_PAYABLE: 'finance.create_payable',
    UPDATE_PAYABLE: 'finance.update_payable',
    CLOSE_PAYABLE: 'finance.close_payable',
    /**
     * Slice 6 PR#4 (CAP-5) — DELETE for the 3-level deletion flow.
     * Same rationale as DELETE_RECEIVABLE above — destructive on
     * payables is admin-only because the soft path reverses any
     * open installment plans, which is a finance-team call.
     */
    DELETE_PAYABLE: 'finance.delete_payable',
    VIEW_COLLECTIONS: 'finance.view_collections',
    CREATE_COLLECTION: 'finance.create_collection',
    /**
     * Destructive correction on a recorded collection (anular / editar
     * un cobro). Kept separate from CREATE_COLLECTION for the same
     * reason DELETE_RECEIVABLE exists: writing a collection is a
     * normal cashier action, but unwinding one that already hit the
     * books and the bank movements is an accounting correction. The
     * role matrix grants it to ADMIN only.
     */
    DELETE_COLLECTION: 'finance.delete_collection',
    ALLOCATE: 'finance.allocate',
    MANAGE_ACCOUNTS: 'finance.manage_accounts',
    MANAGE_BANK_ACCOUNTS: 'finance.manage_bank_accounts',
    MANAGE_RECURRING_EXPENSES: 'finance.manage_recurring_expenses',
    VIEW_REPORTS: 'finance.view_reports',
    VIEW_EQUITY: 'finance.view_equity',
    MANAGE_EQUITY: 'finance.manage_equity',
    /**
     * sdd/finanzas-modulo PR#1 — top-level gate for the Finanzas
     * sidebar entry (`/finance`). Distinct from `finance.view_reports`
     * and `finance.view_equity` because the sidebar needs a single
     * boolean to decide whether to render the parent menu, even if
     * the user is missing one of the sub-permissions. ADMIN gets it
     * implicitly via ALL_PERMISSION_VALUES; SELLER/WAREHOUSE do NOT
     * (the finance module is an ADMIN surface).
     */
    VIEW_FINANCE_MODULE: 'finance.view_finance_module',
  },
  /**
   * Slice 4 — Dispatch / WMS permissions.
   *
   * Naming follows the `<resource>.<action>` contract. Resources map
   * 1:1 onto the new tables introduced in this PR:
   *
   *   dispatches  → `dispatches` + `dispatch_items` (the line items
   *                  are gated through the parent dispatch — the
   *                  resolve_item permission is the per-line accept /
   *                  reject flow used by the warehouse picker).
   *
   * `dispatch.create` is split from `dispatch.approve` because in
   * many tenants the SELLER creates the dispatch (from an APPROVED
   * sales order) and the WAREHOUSE manager approves + marks-ready.
   * `dispatch.mark_ready` is the PREPARING → READY transition (the
   * warehouse has finished picking); `dispatch.cancel` is the only
   * admin-side mutation that can fire from READY (revert a dispatch
   * that was marked-ready but never physically shipped).
   */
  DISPATCH: {
    VIEW: 'dispatch.view',
    CREATE: 'dispatch.create',
    APPROVE: 'dispatch.approve',
    MARK_READY: 'dispatch.mark_ready',
    CANCEL: 'dispatch.cancel',
    RESOLVE_ITEM: 'dispatch.resolve_item',
  },
  /**
   * Slice 4 — Notifications.
   *
   * Two permissions only: every authenticated user gets VIEW (so the
   * bell badge is universal), and only the fan-out service gets SEND
   * (so a misbehaving role cannot forge SYSTEM notifications).
   *
   * Note: `notifications.view` is intentionally broad — there is no
   * per-role VIEW split (e.g. `notifications.view_own`). The route
   * layer scopes each query to the requesting user via the JWT, and
   * RLS scopes to the tenant, so a SELLER never sees ADMIN's
   * notifications even with the same permission.
   */
  NOTIFICATIONS: {
    VIEW: 'notifications.view',
    SEND: 'notifications.send',
  },
  /**
   * Slice 5 — RF-13 International Purchases.
   *
   * Naming follows the `<resource>.<action>` contract. The action
   * vocabulary mirrors the lifecycle state machine:
   *
   *   view            — read access to PO header + line items + tab
   *                     pages; everyone involved in the workflow
   *                     needs this (SELLER, WAREHOUSE, ADMIN).
   *   create          — author a new DRAFT PO; SELLER + ADMIN.
   *   update          — edit a DRAFT (or revert APPROVED→DRAFT per
   *                     AD-9, then edit); SELLER + ADMIN.
   *   approve         — flip DRAFT→APPROVED; ADMIN only (auto-creates
   *                     `payable_items` per CAP-2 R9, mirror of the
   *                     dispatch.approve pattern).
   *   mark_in_transit — flip APPROVED→IN_TRANSIT (vendor has shipped);
   *                     SELLER + ADMIN (warehouse is the receiver,
   *                     not the shipper).
   *   receive         — flip IN_TRANSIT→RECEIVED via the
   *                     `apply-purchase-order-receipt` TX (CAP-3
   *                     prorrateo + WAC + inventory upsert +
   *                     `inventory_movements` insert); WAREHOUSE only.
   *   cancel          — flip DRAFT→CANCELLED (CAP-1 forbids cancel
   *                     from APPROVED); SELLER + ADMIN.
   *   view_movements  — read access to the `inventory_movements` rows
   *                     that the receive TX emitted (for the Movements
   *                     tab on PO detail); SELLER + WAREHOUSE + ADMIN.
   */
  PURCHASES: {
    VIEW: 'purchases.view',
    CREATE: 'purchases.create',
    UPDATE: 'purchases.update',
    APPROVE: 'purchases.approve',
    MARK_IN_TRANSIT: 'purchases.mark_in_transit',
    RECEIVE: 'purchases.receive',
    CANCEL: 'purchases.cancel',
    VIEW_MOVEMENTS: 'purchases.view_movements',
    /**
     * Slice 6 PR#3 — DELETE for the 3-level deletion flow (CAP-4).
     * ADMIN-only — granting deletion of inventory items or purchase
     * orders to SELLER / WAREHOUSE would let any rep erase the
     * historical record (soft-delete + audit). The audit_log row
     * keeps a forensic trail but the policy choice is conservative:
     * destructive actions are admin-only. ADMIN gets the permission
     * implicitly via ALL_PERMISSIONS (see ROLE_PERMISSIONS).
     */
    DELETE: 'purchases.delete',
    /**
     * Anexos — Transit / Consolidated Shipments (Feature A).
     * ADMIN and SELLER can manage shipments (create, add POs, add expenses).
     * WAREHOUSE gets read-only access to view shipment data during receiving.
     */
    MANAGE_SHIPMENTS: 'purchases.manage_shipments',
  },
  /**
   * Slice 8 — Authorizations module (PR#2b).
   *
   * Naming follows the `<resource>.<action>` contract. The vocabulary
   * is intentionally tight: three permissions cover the full
   * authorization lifecycle (templates, requests, responses).
   *
   *   view     — read access to templates list/detail + the inbox
   *              (requests list filtered by visibility) + the
   *              request detail (with attachments + response if
   *              present). Every authenticated user gets this.
   *   create   — author a new authorization template (ADMIN-only
   *              per the RBAC matrix in design section E.1) OR file
   *              a new authorization request (WAREHOUSE-only in v1;
   *              SELLER is VIEW-only). The route layer's
   *              `requirePermission()` middleware wires it.
   *   respond  — flip a PENDING request to APPROVED/REJECTED. Only
   *              ADMINs receive the fan-out notification in v1, so
   *              this is effectively ADMIN-only even though the
   *              permission itself is granted to all three roles
   *              (the visibility check in AuthorizationService
   *              enforces the actual restriction at runtime).
   *
   * The role→permission mapping in packages/domain/src/roles.ts
   * grants the same set to WAREHOUSE and SELLER; ADMIN gets
   * everything via ALL_PERMISSION_VALUES.
   */
  AUTHORIZATIONS: {
    VIEW: 'authorizations.view',
    CREATE: 'authorizations.create',
    RESPOND: 'authorizations.respond',
  },
  /**
   * Sprint 3 PR#3b — Auditoría permission.
   *
   * ADMIN-only read of the `audit_log` table surfaced via the
   * `/audit` page. Mirrors the pattern of `ROLES.VIEW` below: a single
   * leaf permission that gates the route, the FE page, and the
   * sidebar entry. ADMIN receives it implicitly via
   * `ALL_PERMISSION_VALUES` in `@repo/domain/roles.ts`; SELLER +
   * WAREHOUSE are NOT granted the permission, so they see the
   * no-access fallback on the page and the sidebar filters the entry
   * out automatically.
   *
   * The constant is the only place the literal `'audit.view'` lives.
   * The `LeafValues<T>` recursive type picks it up so the `Permission`
   * literal-union widens without a code change at the consumption
   * site — `requirePermission(PERMISSIONS.AUDIT.VIEW)` and
   * `hasPermission(perms, PERMS.AUDIT.VIEW)` both type-check.
   */
  AUDIT: {
    VIEW: 'audit.view',
  },
  /**
   * PR#4a BE — RBAC admin permissions (roles-personalizados).
   *
   * The 5 keys gate the new CRUD surface on the `/api/v1/roles`
   * family of endpoints. ADMIN gets all 5 implicitly via
   * `ALL_PERMISSION_VALUES` in `@repo/domain/roles.ts`; SELLER and
   * WAREHOUSE do NOT get any of them (the legacy `users.manage_roles`
   * key — granted only to ADMIN — is kept for the legacy
   * `/api/v1/roles` read-only catalogue until FE migrates).
   *
   *   view             — list + detail + permission catalog
   *                      (replaces the legacy `users.manage_roles` gate)
   *   create           — POST /api/v1/roles (ADMIN-only per locked decision #1)
   *   update           — PATCH /api/v1/roles/:id (rename displayName + description;
   *                      code is immutable post-create per locked decision #14)
   *   delete           — DELETE /api/v1/roles/:id (ADMIN-only; refuses
   *                      `isSystem=true` rows per locked decision #4)
   *   sync_permissions — PUT /api/v1/roles/:id/permissions (replace the
   *                      role's pivot row set; ADMIN-only)
   *
   * Adding a key here automatically widens the `Permission` literal-union
   * via the `LeafValues<T>` recursive type at the bottom of this file,
   * so `requirePermission(...)` accepts the new keys without further
   * plumbing. The `ALL_PERMISSIONS` flat array picks up the new keys
   * for free too.
   */
  ROLES: {
    VIEW: 'roles.view',
    CREATE: 'roles.create',
    UPDATE: 'roles.update',
    DELETE: 'roles.delete',
    SYNC_PERMISSIONS: 'roles.sync_permissions',
  },
} as const;

// Union-of-all-permission-values. The trick is to distribute the
// type over the union members BEFORE recursing — doing
// `PERMISSIONS[keyof PERMISSIONS][keyof ...]` collapses to the
// intersection of all literal-string values, which is `never` for
// distinct strings.
type LeafValues<T> = T extends object ? LeafValues<T[keyof T]> : T;
export type Permission = LeafValues<typeof PERMISSIONS>;

/**
 * Flat list of every defined permission — useful for tests and for
 * the "show me the permission catalog" admin page in Slice N.
 */
export const ALL_PERMISSIONS: readonly Permission[] = Object.values(PERMISSIONS).flatMap((group) =>
  Object.values(group),
) as unknown as readonly Permission[];
